import JSZip from 'jszip';
import { md5, md5String } from './md5.js';
import { BACKDROP_SVG, CAT_SVG, CAT_SVG_B, SURPRISE_SVGS, emptyVectorCostume, meowSound, popSound } from './assets.js';
import { decompileTarget, uid } from './language.js';

export function bytesOf(data) {
  if (data instanceof Uint8Array) return data;
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  if (typeof data === 'string') return new TextEncoder().encode(data);
  return new Uint8Array(data);
}

export function hashAsset(data, ext) {
  const hash = md5(bytesOf(data));
  return { hash, md5ext: `${hash}.${ext}` };
}

function costumeFromSvg(name, svg, rotationCenter = null) {
  const data = typeof svg === 'string' ? new TextEncoder().encode(svg) : svg;
  const { md5ext, hash } = hashAsset(data, 'svg');
  const w = 95;
  const h = 100;
  return {
    name,
    bitmapResolution: 1,
    dataFormat: 'svg',
    assetId: hash,
    md5ext,
    rotationCenterX: rotationCenter?.[0] ?? w / 2,
    rotationCenterY: rotationCenter?.[1] ?? h / 2,
    _data: data,
    _svg: typeof svg === 'string' ? svg : new TextDecoder().decode(data),
  };
}

function costumeFromPng(name, data, w, h, res = 2) {
  const { md5ext, hash } = hashAsset(data, 'png');
  return {
    name,
    bitmapResolution: res,
    dataFormat: 'png',
    assetId: hash,
    md5ext,
    rotationCenterX: w / 2,
    rotationCenterY: h / 2,
    _data: bytesOf(data),
  };
}

function soundFromWav(name, data, rate = 22050) {
  const bytes = bytesOf(data);
  const { md5ext, hash } = hashAsset(bytes, 'wav');
  return {
    name,
    assetId: hash,
    dataFormat: 'wav',
    format: '',
    rate,
    sampleCount: Math.max(0, Math.floor((bytes.length - 44) / 2)),
    md5ext,
    _data: bytes,
  };
}

export function makeStage() {
  const backdrop = costumeFromSvg('backdrop1', BACKDROP_SVG, [240, 180]);
  backdrop.rotationCenterX = 240;
  backdrop.rotationCenterY = 180;
  return {
    isStage: true,
    name: 'Stage',
    variables: { score: ['score', 0] },
    lists: {},
    broadcasts: {},
    blocks: {},
    comments: {},
    currentCostume: 0,
    costumes: [backdrop],
    sounds: [],
    volume: 100,
    layerOrder: 0,
    tempo: 60,
    videoTransparency: 50,
    videoState: 'on',
    textToSpeechLanguage: null,
    scriptText: 'when flag_clicked():\n    # Stage scripts go here\n',
  };
}

export function makeSprite(name = 'Sprite1') {
  return {
    isStage: false,
    name,
    variables: {},
    lists: {},
    broadcasts: {},
    blocks: {},
    comments: {},
    currentCostume: 0,
    costumes: [
      costumeFromSvg('costume1', CAT_SVG, [48.46, 73.26]),
      costumeFromSvg('costume2', CAT_SVG_B, [48.46, 73.26]),
    ],
    sounds: [soundFromWav('meow', meowSound(), 22050)],
    volume: 100,
    layerOrder: 1,
    visible: true,
    x: 0,
    y: 0,
    size: 100,
    direction: 90,
    draggable: false,
    rotationStyle: 'all around',
    scriptText: `when flag_clicked():
    score = 0
    say_for(join("Hello, ", "world!"), 2)
    move(10)
`,
  };
}

export function defaultProject() {
  const stage = makeStage();
  const sprite = makeSprite('Sprite1');
  sprite.sounds = [soundFromWav('pop', popSound(), 22050)];
  return {
    targets: [stage, sprite],
    monitors: [],
    extensions: [],
    meta: {
      semver: '3.0.0',
      vm: '0.2.0',
      agent: 'Scratch Text Editor',
    },
    _name: 'Untitled',
  };
}

export function surpriseCostume() {
  const svg = SURPRISE_SVGS[Math.floor(Math.random() * SURPRISE_SVGS.length)];
  return costumeFromSvg(`costume${Date.now() % 999}`, svg, [40, 40]);
}

export function paintedCostume(name, svg) {
  return costumeFromSvg(name, svg || emptyVectorCostume(), [50, 50]);
}

export function paintedBitmapCostume(name, pngBytes, w, h) {
  return costumeFromPng(name, pngBytes, w, h, 2);
}

export function surpriseSound() {
  return soundFromWav('pop', popSound(), 22050);
}

function stripInternal(obj) {
  if (Array.isArray(obj)) return obj.map(stripInternal);
  if (obj && typeof obj === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      if (k.startsWith('_')) continue;
      if (k === 'scriptText') continue;
      out[k] = stripInternal(v);
    }
    return out;
  }
  return obj;
}

export async function saveSb3(project) {
  const zip = new JSZip();
  const clean = stripInternal({
    targets: project.targets,
    monitors: project.monitors || [],
    extensions: project.extensions || [],
    meta: project.meta || { semver: '3.0.0', vm: '0.2.0', agent: 'Scratch Text Editor' },
  });
  zip.file('project.json', JSON.stringify(clean));
  const seen = new Set();
  for (const t of project.targets) {
    for (const c of t.costumes || []) {
      if (!c.md5ext || seen.has(c.md5ext)) continue;
      seen.add(c.md5ext);
      const data = c._data || (c._svg ? new TextEncoder().encode(c._svg) : null);
      if (data) zip.file(c.md5ext, data);
    }
    for (const s of t.sounds || []) {
      if (!s.md5ext || seen.has(s.md5ext)) continue;
      seen.add(s.md5ext);
      if (s._data) zip.file(s.md5ext, s._data);
    }
  }
  return zip.generateAsync({ type: 'uint8array', compression: 'DEFLATE' });
}

export async function loadSb3(buffer) {
  const zip = await JSZip.loadAsync(buffer);
  const jsonFile = zip.file('project.json');
  if (!jsonFile) throw new Error('Not a valid .sb3 (missing project.json)');
  const project = JSON.parse(await jsonFile.async('string'));
  for (const t of project.targets) {
    for (const c of t.costumes || []) {
      const f = zip.file(c.md5ext) || zip.file(`${c.assetId}.${c.dataFormat}`);
      if (f) {
        c._data = await f.async('uint8array');
        if (c.dataFormat === 'svg') c._svg = new TextDecoder().decode(c._data);
      }
    }
    for (const s of t.sounds || []) {
      const f = zip.file(s.md5ext) || zip.file(`${s.assetId}.${s.dataFormat}`);
      if (f) s._data = await f.async('uint8array');
    }
    decompileTarget(t, project);
  }
  project._name = project.meta?.title || 'Untitled';
  return project;
}

export function uniqueName(base, existing) {
  if (!existing.includes(base)) return base;
  let i = 2;
  while (existing.includes(`${base}${i}`)) i++;
  return `${base}${i}`;
}

export function addVariable(project, target, name, local, isList) {
  const id = uid();
  if (isList) {
    (local ? target : project.targets[0]).lists[id] = [name, []];
  } else {
    (local ? target : project.targets[0]).variables[id] = [name, 0];
  }
  return id;
}

export function allVariables(project, target) {
  const out = [];
  const stage = project.targets.find((t) => t.isStage);
  for (const [id, rec] of Object.entries(stage?.variables || {})) {
    out.push({ id, name: rec[0], value: rec[1], local: false, list: false });
  }
  if (target && !target.isStage) {
    for (const [id, rec] of Object.entries(target.variables || {})) {
      out.push({ id, name: rec[0], value: rec[1], local: true, list: false });
    }
  }
  return out;
}

export function allLists(project, target) {
  const out = [];
  const stage = project.targets.find((t) => t.isStage);
  for (const [id, rec] of Object.entries(stage?.lists || {})) {
    out.push({ id, name: rec[0], value: rec[1], local: false, list: true });
  }
  if (target && !target.isStage) {
    for (const [id, rec] of Object.entries(target.lists || {})) {
      out.push({ id, name: rec[0], value: rec[1], local: true, list: true });
    }
  }
  return out;
}

export { costumeFromSvg, costumeFromPng, soundFromWav };
export { md5String };
